// src/services/approvalsService.ts — the maker-checker approval engine's console
// surface (/console/approvals). Reads the filtered listing on
// GET /admin/approvals?status=&page=&limit= (pending arrives in FIFO queue order,
// decided newest-first, actor identities populated) and drives the decision
// endpoints: approve (optional comment), reject (reason mandatory — the API
// refuses without one) and cancel (initiator only). The server enforces the real
// rules — self-approval ban, double-approve ban, required roles, expiry — the UI
// mirrors them for honest affordances, never as the actual gate.
import { message } from "antd";
import { useQueryClient } from "@tanstack/react-query";
import { useApiQuery } from "../hooks/useApiRequest";
import { fetcher } from "../hooks/fetcher";
import {
  APPROVALS_ENDPOINT,
  approvalDecisionRequest,
  type ApprovalDecisionRequest,
} from "./approvalDecisionRequest";
import {
  isSettledByStaffConfirmation,
  staffConfirmationHost,
  type StaffConfirmationHostOptions,
} from "./staffConfirmation/valueMovingConfirmation";

export type ApprovalStatus =
  | "pending"
  | "approved"
  | "rejected"
  | "expired"
  | "cancelled";

export interface ApprovalActor {
  _id?: string;
  email?: string;
  username?: string;
}

export interface ApprovalEntry {
  userId?: ApprovalActor | string;
  role?: string;
  at?: string;
  comment?: string;
  reason?: string;
}

export interface AdminApproval {
  _id: string;
  /** dot-namespaced action key, e.g. "withdrawal.approve" */
  action: string;
  targetType: string;
  targetId?: string;
  requiredApprovals?: number;
  requiredRoleKeys?: string[];
  approvals?: ApprovalEntry[];
  rejections?: ApprovalEntry[];
  status: ApprovalStatus;
  createdBy?: ApprovalActor | string;
  expiresAt?: string;
  consumedAt?: string;
  metadata?: Record<string, unknown>;
  createdAt?: string;
  updatedAt?: string;
}

export interface ApprovalsPage {
  items: AdminApproval[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export const APPROVALS_KEY = "console-approvals";
const ENDPOINT = APPROVALS_ENDPOINT;
const POLL_MS = 30_000;

export const useApprovalsList = (
  status: ApprovalStatus | "all",
  page = 1,
  limit = 20
) =>
  useApiQuery<ApprovalsPage>({
    key: [APPROVALS_KEY, status, page, limit],
    endPoint: `${ENDPOINT}?status=${status}&page=${page}&limit=${limit}`,
    // Only the live queue needs near-real-time refresh — decided records are
    // immutable, so history views don't poll.
    queryOptions: {
      refetchInterval: status === "pending" ? POLL_MS : false,
    },
  });

/** Cheap live total for the sidebar badge — shares the APPROVALS_KEY family so
 *  every decision invalidates it alongside the lists. */
export const usePendingApprovalsTotal = () => {
  const query = useApiQuery<ApprovalsPage>({
    key: [APPROVALS_KEY, "badge"],
    endPoint: `${ENDPOINT}?status=pending&page=1&limit=1`,
    queryOptions: { refetchInterval: 60_000 },
  });
  return { total: query.data?.total ?? 0 };
};

const getErrorMessage = (e: unknown): string =>
  e instanceof Error ? e.message : "Something went wrong";

export const useApprovalActions = () => {
  const queryClient = useQueryClient();

  // The call is built by approvalDecisionRequest, the same builder whose hash
  // the approver is shown (ENG-1527), so what is sent is what was shown.
  const run = async (
    request: ApprovalDecisionRequest,
    successNote: string,
    confirmation?: StaffConfirmationHostOptions
  ): Promise<boolean> => {
    try {
      // Approve and reject always carry the prompt opt-in (request config,
      // never the wire). The requester's Cancel carries none: today's call.
      await (confirmation
        ? fetcher(
            request.path,
            request.method,
            request.body,
            undefined,
            confirmation
          )
        : fetcher(request.path, request.method, request.body));
      await queryClient.invalidateQueries({ queryKey: [APPROVALS_KEY] });
      message.success(successNote);
      return true;
    } catch (e) {
      if (!isSettledByStaffConfirmation(e)) message.error(getErrorMessage(e));
      return false;
    }
  };

  // The checker's decision can move value: an approval runs the request it approves.
  // While "Confirm with Google" is on, approve and reject each ask for the
  // approver's OWN staff confirmation, one per decision, bound to that call:
  // its path carries the approval id and the decision, so an approve can never
  // stand in for a reject (ENG-1515). A requester's confirmation is never
  // reused: each click starts its own. Cancel is the requester withdrawing
  // their own request and asks for none.
  return {
    approveApproval: (id: string, comment?: string) =>
      run(
        approvalDecisionRequest(id, "approve", comment),
        "Approval recorded.",
        staffConfirmationHost("Approving this request")
      ),
    rejectApproval: (id: string, reason: string) =>
      run(
        approvalDecisionRequest(id, "reject", reason),
        "Request rejected.",
        staffConfirmationHost("Rejecting this request")
      ),
    cancelApproval: (id: string, reason?: string) =>
      run(approvalDecisionRequest(id, "cancel", reason), "Request cancelled."),
  };
};
